import { memo, Suspense, useMemo } from 'react'
import { motion, LazyMotion, domAnimation } from 'framer-motion'
import { CheckCircle, Target, TrendingUp, FileText, Users } from 'lucide-react'
import { useLanguage } from '../context/LanguageContext'
import { useTranslation } from '../utils/translations'

// Animation légère optimisée
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
}

const Conseil = () => {
  const { language } = useLanguage()
  const t = useTranslation(language)

  // Mémoïsation des listes pour éviter les recalculs
  const missions = useMemo(
    () => [
      'conseil.mission1',
      'conseil.mission2',
      'conseil.mission3',
      'conseil.mission4',
      'conseil.mission5',
      'conseil.mission6',
      'conseil.mission7'
    ],
    []
  )

  const approach = useMemo(
    () => [
      { icon: Target, titleKey: 'conseil.step1Title', descriptionKey: 'conseil.step1Desc' },
      { icon: FileText, titleKey: 'conseil.step2Title', descriptionKey: 'conseil.step2Desc' },
      { icon: TrendingUp, titleKey: 'conseil.step3Title', descriptionKey: 'conseil.step3Desc' },
      { icon: Users, titleKey: 'conseil.step4Title', descriptionKey: 'conseil.step4Desc' }
    ],
    []
  )

  return (
    <div className="pt-26">
      <LazyMotion features={domAnimation}>
        <Suspense fallback={<div className="text-center py-20 text-gray-500">Chargement...</div>}>

          {/* HERO */}
          <section className="relative py-20 bg-gradient-to-br from-blue-600 to-blue-800 text-white overflow-hidden">
            <img
              src="/images/conseil-hero.webp"
              alt="Conseil Stratégique"
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
              decoding="async"
            />
            <div className="absolute inset-0 bg-blue-900/70"></div>

            <div className="relative max-w-7xl mx-auto px-4 text-center">
              <motion.h1
                variants={fadeIn}
                initial="hidden"
                animate="show"
                transition={{ duration: 0.5 }}
                className="text-5xl md:text-6xl font-heading font-bold mb-6"
              >
                {t('conseil.pageTitle')}
              </motion.h1>

              <motion.p
                variants={fadeIn}
                initial="hidden"
                animate="show"
                transition={{ delay: 0.2 }}
                className="text-xl md:text-2xl max-w-3xl mx-auto"
              >
                {t('conseil.pageSubtitle')}
              </motion.p>
            </div>
          </section>

          {/* MISSIONS */}
          <section className="py-20 bg-white">
            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 px-6">

              {/* Image */}
              <motion.div
                variants={fadeIn}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <img
                  src="/images/services/Gemini_Generated_Image_5lvegq5lvegq5lve.webp"
                  alt="Missions de Conseil"
                  className="rounded-2xl shadow-xl w-full h-[450px] object-cover"
                  loading="lazy"
                  decoding="async"
                />
              </motion.div>

              {/* Liste Missions */}
              <div>
                <h2 className="text-4xl font-heading font-bold mb-4 text-gray-900">
                  {t('conseil.missionsTitle')}
                </h2>
                <p className="text-lg text-gray-600 mb-8">
                  {t('conseil.missionsSubtitle')}
                </p>

                <div className="space-y-4">
                  {missions.map((missionKey) => (
                    <div
                      key={missionKey}
                      className="flex items-start space-x-3 bg-gray-50 p-4 rounded-xl hover:shadow transition"
                    >
                      <CheckCircle className="text-blue-600 mt-1" size={22} />
                      <p className="text-gray-700">{t(missionKey)}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* APPROCHE */}
          <section className="py-20 bg-gray-50">
            <div className="max-w-7xl mx-auto px-6">
              <h2 className="text-4xl font-heading font-bold text-center mb-12">
                {t('conseil.approachTitle')}
              </h2>

              <div className="space-y-16">
                {approach.map((step, i) => {
                  const Icon = step.icon
                  return (
                    <div
                      key={step.titleKey}
                      className={`grid grid-cols-1 lg:grid-cols-2 gap-10 items-center ${i % 2 === 1 ? 'lg:grid-flow-dense' : ''}`}
                    >
                      <div className={i % 2 === 1 ? 'lg:col-start-2' : ''}>
                        <img
                          src={`/images/conseil-step-${i + 1}.webp`}
                          alt={t(step.titleKey)}
                          className="rounded-2xl shadow-xl w-full h-[350px] object-cover"
                          loading="lazy"
                          decoding="async"
                        />
                      </div>

                      <div className={i % 2 === 1 ? 'lg:col-start-1' : ''}>
                        <div className="bg-white p-8 rounded-2xl shadow">
                          <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl mb-5">
                            <Icon className="text-white" size={28} />
                          </div>
                          <h3 className="text-2xl font-heading font-bold mb-3">{t(step.titleKey)}</h3>
                          <p className="text-gray-600 text-lg">{t(step.descriptionKey)}</p>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="relative py-20 bg-gradient-to-br from-blue-600 to-blue-800 text-white text-center overflow-hidden">
            <img
              src="/images/conseil-cta.webp"
              alt="Transformation Industrielle"
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
              decoding="async"
            />
            <div className="absolute inset-0 bg-blue-900/80"></div>

            <div className="relative max-w-3xl mx-auto px-6">
              <h2 className="text-3xl md:text-4xl font-heading font-bold mb-6">
                {t('conseil.closingTitle')}
              </h2>
              <p className="text-lg mb-8">{t('conseil.closingText')}</p>
              <motion.a
                href="/contact"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-flex items-center px-8 py-4 bg-white text-blue-700 font-bold rounded-xl shadow hover:shadow-xl transition"
              >
                {t('conseil.cta')}
                <TrendingUp className="ml-2 w-5 h-5" />
              </motion.a>
            </div>
          </section>

        </Suspense>
      </LazyMotion>
    </div>
  )
}

export default memo(Conseil)
